<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'coachingOverview' }"
          marginBottom="5"/>

      <h6 class="element-header">Übersicht</h6>

      <div class="element-box">
        <h5 class="form-header mb-4">Private Weiterbildung</h5>

        <Menu/>

        <Search
            :filters="filters"
            :loading="isSearching"
            :unemployedDropdown="unemployedDropdown"
            :officeDropdown="officeDropdown"
            :coachDropdown="coachDropdown"
            @search="getCoachingList"/>

        <div class="row align-items-center justify-content-between">
          <div class="col-auto">
            <PerPage
                :values="[10, 25, 50, 100]"
                :perPage="perPageValue"
                @update:perPage="getCoachingList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-3">
          <table class="table table-striped">
            <thead>
            <tr>
              <th></th>
              <th></th>
              <th>
                <a href="#" @click.prevent="sortBy('status')">
                  <span>status</span>
                  <i :class="getIconClass('status')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('firstName')">
                  <span>Vorname</span>
                  <i :class="getIconClass('firstName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('lastName')">
                  <span>Nachname</span>
                  <i :class="getIconClass('lastName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('operationType')">
                  <span>Maßnahmenart</span>
                  <i :class="getIconClass('operationType')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('aaNumber')">
                  <span>JC/AA Nr</span>
                  <i :class="getIconClass('aaNumber')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('location')">
                  <span>Standort</span>
                  <i :class="getIconClass('location')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('clerk')">
                  <span>Sachbearbeiter</span>
                  <i :class="getIconClass('clerk')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('startDate')">
                  <span>Start</span>
                  <i :class="getIconClass('startDate')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('endDate')">
                  <span>Ende</span>
                  <i :class="getIconClass('endDate')"></i>
                </a>
              </th>
              <th>
                <a href="#" class="text-end" @click.prevent="sortBy('unitsDone')">
                  <span>Stand</span>
                  <i :class="getIconClass('unitsDone')"></i>
                </a>
              </th>
              <th>
                <a href="#" class="text-end" @click.prevent="sortBy('unitsDone')">
                  <span>Stand %</span>
                  <i :class="getIconClass('unitsDone')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('lastUnit')">
                  <span> Letzter Termin</span>
                  <i :class="getIconClass('lastUnit')"></i>
                </a>
              </th>
              <th>CV/GEVA</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="coaching in coachingList.data" :key="coaching.id">
              <td>
                <ReportButton
                    :to="{ name: 'privateLearningCoachingProtocolList', params: { id: coaching.id } }"/>
              </td>
              <th>
                <TypedPopover
                    :clientData="coaching.client"
                    :userRole="currentUserRole"
                    maxWidth="450px"
                    v-if="coaching.client?.specialFeatures || coaching.client?.specialFeaturesForCoach">
                  <template #trigger>
                    <span class="exclamation-badge">!</span>
                  </template>
                </TypedPopover>
              </th>
              <td>{{ coaching?.statusTranslation }}</td>
              <td>{{ coaching?.unemployed?.firstName }}</td>
              <td>{{ coaching?.unemployed?.lastName }}</td>
              <td>{{ coaching?.operationTypeTranslation }}</td>
              <td>{{ coaching?.unemployed?.aaNumber }}</td>
              <td>{{ coaching?.unemployed?.location?.name }}</td>
              <td>{{ coaching?.clerk }}</td>
              <td>{{ coaching?.startDate }}</td>
              <td>{{ coaching?.endDate }}</td>
              <td class="text-end">{{ coaching?.unitsDone }}</td>
              <td class="text-end">{{ coaching?.unitsDonePercent }} %</td>
              <td>{{ coaching?.lastUnit }}</td>
              <td>
                <ReportButton
                    :to="{ name: 'unemployedDocumentUpload', params: {
                      id: Number(coaching.unemployed.id),
                      routeForBack: 'privateLearningCoachingFinishedList'
                    } }"
                    label="CV/GEVA"
                    :icon="false"
                    letterSpacing="1.5px"/>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="coachingList?.pagination?.total_pages > 1"
            :totalPages="coachingList?.pagination?.total_pages"
            :currentPage="coachingList?.pagination?.current_page"
            :totalItems="coachingList?.pagination?.total_items"
            :perPage="coachingList?.pagination?.items_per_page"
            @pagechanged="getCoachingList"/>
      </div>
    </div>
  </PageTemplate>
</template>


<script setup lang="ts">
import overviewCoachingService from '@/api/CoachingOverview/PrivateLearningCoaching';
import CreateButton from '@/components/CreateButton.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
// @ts-ignore
import {getFinishedPrivateLearningCoachingDropdown, getOfficeByUserRoleDropdown, getUserDropdown} from '@/shared/dropdowns';
// @ts-ignore
import {downloadFile, getSortIconClass} from '@/shared/functions';
import Search from '@/views/App/CoachingOverview/PrivateLearningCoaching/Finished/Search.vue';
import Menu from '@/views/App/CoachingOverview/PrivateLearningCoaching/Menu.vue';
import {computed, onMounted, ref} from 'vue';
import {
  ICoachingOverviewList,
  IPagination
} from "@/interfaces/CoachingOverview/PrivateLearningCoaching/ICoachingOverview";
import {ICoachingOverviewFilters} from "@/interfaces/CoachingOverview/ICoachingOverviewFIlters";
import {piniaUserStore} from "@/store/piniaUserStore";
import ClientSpecialFeaturesPopover from "@/components/ClientSpecialFeaturesPopover.vue";
import BackButton from "@/components/button/BackButton.vue";
import ReportButton from "@/components/button/ReportButton.vue";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const piniaUserState = piniaUserStore();
const piniaCurrentUser = computed(() => piniaUserState.currentUser.user);
const currentUserRole = piniaCurrentUser.value?.roles?.[0];
const isExcelLoaded = ref(false);

const TypedPopover = ClientSpecialFeaturesPopover as unknown as {
  new(): {
    $slots: {
      trigger: () => any
      content: () => any
    }
  }
}

const coachingList = ref<ICoachingOverviewList>({
  data: [],
  pagination: {
    current_page: 1,
    items_per_page: 10,
    total_pages: 0,
  } as IPagination,
});

const filters = ref<ICoachingOverviewFilters>({
  page: 1,
  limit: 10,
  sortBy: 'firstName',
  searchTerm: '',
  orderBy: 'ASC',
});

const unemployedDropdown = ref([]);
const coachDropdown = ref([]);
const officeDropdown = ref([]);
const isSearching = ref(false);
const isLoaded = ref(false);
const perPageValue = ref(25)

async function getCoachingList(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: ICoachingOverviewFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachingList.value?.pagination?.items_per_page;

  perPageValue.value = perPage ?? perPageRes;

  try {
    const {data} = await overviewCoachingService.getFinishedList({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes,
    });

    coachingList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function getIconClass(key: string) {
  return getSortIconClass(filters.value, key);
}

async function sortBy(key: string) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy:
        filters.value.orderBy === 'DESC' ||
        filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  await getCoachingList();
}

async function getExcelExport() {
  await downloadFile('/overview-private-learning-coaching/finished-excel-export',
      undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getCoachingList(1, perPageValue.value),
    getFinishedPrivateLearningCoachingDropdown(unemployedDropdown),
    getUserDropdown(coachDropdown),
    getOfficeByUserRoleDropdown(officeDropdown),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>
/* Add cursor pointer */
.stand_popover {
  cursor: pointer;
}
</style>